/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-12-10 16:23:55                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-27 13:15:13                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * Services
  * Validator
  * ContactForm: Contact form component
    * handleChange: handle field change event
*/

/* Imports */
import { t } from "i18next";
import Select from "react-select";
import { ContactValidator } from "./contact-validator";
/***/

/* Components */
import FormDropdown from "../../form-dropdown/FormDropdown";
import ContactInfoForm, { ContactInfoFormValidator } from "../contact-infos/ContactInfoForm";
import { embeddedFormChange } from "../../../services/utils/utils";
import { useEffect, useState } from "react";
import { CIVILITY_CODES, normalizeCivility } from "./civility";
/***/

/* Services */
import { validator } from "../../../services/form-validator/form-validator";
/***/

/* Validator */
export const ContactFormValidator = {
  firstName: (e: unknown) => validator.isString(e),
  lastName: (e: unknown) => validator.isString(e),
  function: (e: unknown) => validator.isString(e),
  civility: (e: unknown) => validator.isString(e),
  contactInfo: (e: unknown) => validator.isFormValid(e, ContactInfoFormValidator)
};

const v = ContactFormValidator; // shorthand
/***/

/**
* ContactForm: Contact form component
* allow creation and edition of contact entity 
* @param data - data to edit, null to creation
* @param onChange - on value change event
* @param onSubmit - on submit event
* @param submitted - is form submitted
*/
export default function ContactForm({
  data,
  onChange,
  onSubmit,
  submitted
}: {
  data?: EasyTech.Contact,
  onChange: (name: string, value: any) => void,
  onSubmit: () => void,
  submitted?: boolean
}) {
  // contactInfo est cloné : embeddedFormChange écrit dedans en place, et sans copie
  // la saisie modifiait directement le contact affiché dans la liste, même à l'annulation.
  const [dataState, setDataState] = useState<EasyTech.Contact>({
    ...data,
    contactInfo: {
      email: "",
      phone: "",
      fax: "",
      notes: "",
      ...data?.contactInfo
    }
  } as EasyTech.Contact);

  const validateFormData = (formData: any): boolean => {
    if (!formData) return false;
    const validationErrors = ContactValidator.validate(formData, t);
    return validationErrors.length === 0;
  };

  const handleSubmitWithValidation = () => {
    if (submitted && !validateFormData(data)) {
      console.warn("Validation échouée - champs obligatoires manquants");
      return;
    }
    onSubmit();
  };

  /**
  * handleChange: handle field change event
  * @param name - field name
  * @param value - param value
  */
  const handleChange = (name: string, value: any) => {
    const tmp = {...dataState};
    tmp[name] = value;
    setDataState(tmp);
    onChange(name, value);
  };

  const civility = normalizeCivility(data?.civility);
  const civilityOptions = CIVILITY_CODES.map((code) => ({
    value: code,
    label: t(`contact:form.fields.civility.options.${code}`)
  }));

  useEffect(() => {
    if (civility && data?.civility !== civility) {
      handleChange("civility", civility);
    }
  }, [data?.civility]);
  /***/

  return (
    <>
      <FormDropdown label={t("workorder:tabs.informations")}>
        <div className="field">
          <label>{t("contact:form.fields.firstname.label")} *</label>
          <input  type="text"
                  className={`form-field ${submitted && !validator.isFieldValid(data, "firstName", v) && "error"}`}
                  placeholder={t("contact:form.fields.firstname.placeholder")}
                  name="firstName"
                  value={data?.firstName}
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  onKeyDown={(e) => e.key === "ENTER" && handleSubmitWithValidation()}/>
        </div>

        <div className="field">
          <label>{t("contact:form.fields.lastname.label")} *</label>
          <input  type="text"
                  className={`form-field ${submitted && !validator.isFieldValid(data, "lastName", v) && "error"}`}
                  placeholder={t("contact:form.fields.lastname.placeholder")}
                  name="lastName"
                  value={data?.lastName}
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  onKeyDown={(e) => e.key === "ENTER" && handleSubmitWithValidation()}/>
        </div>

        <div className="field">
          <label>{t("contact:form.fields.function.label")} *</label>
          <input  type="text"
                  className={`form-field ${submitted && !validator.isFieldValid(data, "function", v) && "error"}`}
                  placeholder={t("contact:form.fields.function.placeholder")}
                  name="function"
                  value={data?.function}
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  onKeyDown={(e) => e.key === "ENTER" && handleSubmitWithValidation()}/>
        </div>

        <div className="field">
          <label>{t("contact:form.fields.civility.label")} *</label>
          <Select value={civilityOptions.find((option) => option.value === civility)}
                  className={`min-w-0 w-full ${submitted && !validator.isFieldValid(data, "civility", v) && "error"}`}
                  classNamePrefix="react-select"
                  theme={(theme) => ({
                    ...theme,
                    colors: {
                      ...theme.colors,
                      primary: "#41A1FF",
                    },
                  })}
                  options={civilityOptions}
                  placeholder={t("contact:form.fields.civility.placeholder")}
                  onChange={(e) => handleChange("civility", e?.value)}/>
        </div>
      </FormDropdown>

      <FormDropdown label={t("common:contact")}>
        <ContactInfoForm  data={{
                            ...(data?.contactInfo || {email: "", phone: "", fax: "", notes: ""}),
                            notes: data?.notes || ""
                          }}
                          onSubmit={handleSubmitWithValidation}
                          onChange={(name, value) => {
                            if (name === "notes") {
                              handleChange("notes", value);
                            } else {
                              embeddedFormChange(dataState, "contactInfo", name, value, handleChange);
                            }
                          }}
                          submitted={submitted}/>
      </FormDropdown>
    </>
  );
}
/***/
